// src/pages/admin/UserForm.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api";
import AddressFields from "../../components/common/address/AddressFields";
import { DatePicker } from '../../components/common/DatePicker';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import PhoneNumberFields from '../../components/common/PhoneNumberFields';
import { validateStoredPhone } from '../../utils/phone';
import { TextInput, SelectInput, CheckboxField, FormField } from '../../components/common/form';
import { toISODateFromRegional, toRegionalDateFromISO } from '../../utils/date';


import {
    addressPayloadFields,
    hasAnyAddress,
    validateAddressIfPresent,
} from "../../components/common/address/addressValidators";

const ALL_ROLES = ["ADMIN", "MANAGER", "COACH", "STUDENT"];

const UserForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [loadError, setLoadError] = useState("");

    const [form, setForm] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        dob: "",
        gender: "MALE",
        username: "",
        email: "",
        phoneNumber: "",
        roles: [],
        active: true,
        addressId: null,
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        latitude: "",
        longitude: "",
    });

    const [sendInvite, setSendInvite] = useState(true);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isEditMode) return;
        setInitialLoading(true);
        setLoadError("");
        API.get(`/admin/users/${id}`)
            .then((res) => {
                const u = res.data || {};
                setForm({
                    firstName: u.firstName || "",
                    middleName: u.middleName || "",
                    lastName: u.lastName || "",
                    dob: u.dob ? toRegionalDateFromISO(u.dob) : "",
                    gender: u.gender || "MALE",
                    username: u.username || "",
                    email: u.email || "",
                    phoneNumber: u.phoneNumber || "",
                    roles: Array.isArray(u.roles) ? u.roles : [],
                    active: u.active ?? true,
                    addressId: u.address?.id ?? null,
                    addressLine1: u.address?.addressLine1 || "",
                    addressLine2: u.address?.addressLine2 || "",
                    city: u.address?.city || "",
                    state: u.address?.state || "",
                    country: u.address?.country || '',
                    pincode: u.address?.pincode || "",
                    latitude: u.address?.latitude != null ? String(u.address.latitude) : "",
                    longitude: u.address?.longitude != null ? String(u.address.longitude) : "",
                });
            })
            .catch(() => setLoadError(t('forms.user.loadFailed')))
            .finally(() => setInitialLoading(false));
    }, [id, isEditMode, t]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const toggleRole = (role) => {
        const r = String(role).toUpperCase();
        setForm((f) => {
            const has = f.roles.includes(r);
            return { ...f, roles: has ? f.roles.filter(x => x !== r) : [...f.roles, r] };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (form.roles.length === 0) {
            setError(t('forms.validation.rolesRequired'));
            return;
        }
        if (!sendInvite) {
            if (!password || password !== confirmPassword) {
                setError(t('forms.validation.passwordsMismatch'));
                return;
            }
        }

        const addrErr = validateAddressIfPresent({
            addressLine1: form.addressLine1, addressLine2: form.addressLine2,
            city: form.city, state: form.state, country: form.country,
            pincode: form.pincode, latitude: form.latitude, longitude: form.longitude,
        });
        if (addrErr) {
            setError(addrErr);
            return;
        }

        if (form.phoneNumber) {
            const phoneCheck = validateStoredPhone(form.phoneNumber);
            if (!phoneCheck.valid) {
                setError(phoneCheck.message);
                return;
            }
        }

        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };

        const payload = {
            ...form,
            phoneNumber: form.phoneNumber
                ? validateStoredPhone(form.phoneNumber).normalized
                : form.phoneNumber,
            roles: form.roles,
            sendInvite: sendInvite,
            password: sendInvite ? undefined : password,
            dob: form.dob
                ? (toISODateFromRegional(form.dob) || form.dob)
                : null,
            ...addressPayloadFields(addressObj, { addressId: form.addressId }),
        };

        try {
            if (isEditMode) {
                await API.put(`/admin/users/${id}`, payload);
            } else {
                await API.post(`/admin/users`, payload);
            }
            navigate("/admin/users", { replace: true });
        } catch (err) {
            setError(t('forms.user.saveFailed'));
        }
    };

    const RoleChip = ({ role }) => {
        const active = form.roles.includes(role);
        return (
            <button
                type="button"
                onClick={() => toggleRole(role)}
                className={`px-3 py-1 rounded border ${active ? "bg-purple-700 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
            >
                {t(`faq.roles.${role}`, { defaultValue: role })}
            </button>
        );
    };

    if (initialLoading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEditMode ? t('forms.user.editTitle') : t('forms.user.addTitle')}
                subtitle={t('forms.user.subtitle')}
            />
        );
    }

    if (isEditMode && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.user.editTitle')}
                    subtitle={t('forms.user.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError}
                </div>
                <div className="mt-4">
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/users')}>
                        {t('common.back', { defaultValue: 'Back' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={isEditMode ? t('forms.user.editTitle') : t('forms.user.addTitle')}
                subtitle={t('forms.user.subtitle')}
            />

            {error && <div className="mb-3 text-red-600">{error}</div>}

            <form onSubmit={handleSubmit} className="form-shell space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <TextInput label={t('forms.common.firstName')} name="firstName" value={form.firstName} onChange={handleChange} placeholder={t('forms.placeholders.firstName')} />
                    <TextInput label={t('forms.common.middleName')} name="middleName" value={form.middleName} onChange={handleChange} placeholder={t('forms.placeholders.middleName')} />
                    <TextInput label={t('forms.common.lastName')} name="lastName" value={form.lastName} onChange={handleChange} placeholder={t('forms.placeholders.lastName')} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FormField label={t('forms.common.dateOfBirth')}>
                        <DatePicker value={form.dob}
                            onChange={(date) =>
                                setForm((prev) => ({
                                    ...prev,
                                    dob: date
                                }))
                            } className="w-full" />
                    </FormField>
                    <SelectInput
                        label={t('forms.common.gender')}
                        name="gender"
                        value={form.gender}
                        onChange={handleChange}
                        required
                    >
                        <option value="MALE">{t('forms.common.male')}</option>
                        <option value="FEMALE">{t('forms.common.female')}</option>
                        <option value="OTHER">{t('forms.common.other')}</option>
                    </SelectInput>
                    <TextInput label={t('forms.common.loginUsername')} name="username" value={form.username} onChange={handleChange} placeholder={t('forms.placeholders.username')} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextInput label={t('forms.common.emailAddress')} name="email" type="email" value={form.email} onChange={handleChange} placeholder={t('forms.placeholders.email')} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FormField label={t('forms.common.phoneNumber')}>
                        <PhoneNumberFields
                            value={form.phoneNumber}
                            onChange={(v) => setForm((f) => ({ ...f, phoneNumber: v }))}
                        />
                    </FormField>
                    <CheckboxField
                        layout="toggle-card"
                        label={t('forms.common.accountActive')}
                        checked={form.active}
                        onChange={() => setForm({ ...form, active: !form.active })}
                    />
                </div>

                <div>
                    <div className="text-sm text-gray-600 mb-1">{t('forms.common.roles')}</div>
                    <div className="flex flex-wrap gap-2">
                        {ALL_ROLES.map(r => <RoleChip key={r} role={r} />)}
                    </div>
                </div>

                {!isEditMode && (
                    <div className="space-y-3">
                        <CheckboxField
                            label={t('forms.user.emailSignInLink')}
                            checked={sendInvite}
                            onChange={() => setSendInvite(!sendInvite)}
                        />
                        {!sendInvite && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <TextInput
                                    label={t('forms.common.loginPassword')}
                                    type="password"
                                    placeholder={t('forms.placeholders.password')}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                />
                                <TextInput
                                    label={t('forms.common.confirmLoginPassword')}
                                    type="password"
                                    placeholder={t('forms.placeholders.confirmPassword')}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                )}

                <AddressFields
                    value={form}
                    applyDefaultCountry={!isEditMode}
                    onChange={(patchOrName, maybeValue) => {
                        if (typeof patchOrName === 'string') {
                            setForm((prev) => ({ ...prev, [patchOrName]: maybeValue }));
                        } else if (patchOrName && typeof patchOrName === 'object') {
                            setForm((prev) => ({ ...prev, ...patchOrName }));
                        }
                    }}
                />

                <div className="flex justify-end pt-2">
                    <Button type="submit" variant="primary">
                        {t(isEditMode ? 'forms.user.updateButton' : 'forms.user.createButton')}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default UserForm;